import {createServerFn} from '@tanstack/react-start';
import {
  getRequestUser,
  getSupabaseServerClient,
} from '@/helpers/supabaseServer.server';
import {authMiddleware} from '@/helpers/authMiddleware';
import {prismaClient} from '@/helpers/prismaClient.server';
import {createPlunkContact} from '@/helpers/plunkClient.server';
import {extractRequestContext, trackEvent} from '@/helpers/mixpanel.server';
import {Profile} from '@/apiTypes/profile';
import {transposeSubscriptions} from '@/server/subscription';
import {z} from 'zod';
import {getRequest} from '@tanstack/react-start/server';

const LoginInput = z.object({
  email: z.string().email(),
  password: z.string().min(6),
});

/**
 * Signs in a user with email/password on the server.
 * Cookies are set server-side in the same response, avoiding client/server race conditions.
 */
export const loginWithPassword = createServerFn({method: 'POST'})
  .inputValidator((data: unknown): z.infer<typeof LoginInput> => {
    const result = LoginInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data}): Promise<{error: string | null}> => {
    console.log('[auth/loginWithPassword] start', {email: data.email});

    const supabase = await getSupabaseServerClient();
    console.log('[auth/loginWithPassword] supabase client ready');

    const {error} = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });

    if (error) {
      console.error('[auth/loginWithPassword] supabase signIn error', {
        code: error.code,
        status: error.status,
        message: error.message,
      });
      return {error: error.message};
    }

    console.log('[auth/loginWithPassword] success', {email: data.email});
    return {error: null};
  });

const SignupInput = z.object({
  email: z.string().email(),
  password: z.string().min(6),
  emailRedirectTo: z.string().url(),
});

/**
 * Creates a new user account with email/password on the server.
 * Cookies are set server-side in the same response.
 */
export const signupWithPassword = createServerFn({method: 'POST'})
  .inputValidator((data: unknown): z.infer<typeof SignupInput> => {
    const result = SignupInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(
    async ({
      data,
    }): Promise<{error: string | null; needsConfirmation: boolean}> => {
      console.log('[auth/signupWithPassword] start', {
        email: data.email,
      });

      const supabase = await getSupabaseServerClient();
      console.log('[auth/signupWithPassword] supabase client ready');

      const {data: authData, error} = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: data.emailRedirectTo,
        },
      });

      if (error) {
        console.error('[auth/signupWithPassword] supabase signUp error', {
          code: error.code,
          status: error.status,
          message: error.message,
        });
        return {error: error.message, needsConfirmation: false};
      }

      console.log('[auth/signupWithPassword] supabase signUp ok', {
        hasSession: !!authData.session,
        userId: authData.user?.id,
      });

      // If email confirmation is required, session will be null
      if (!authData.session) {
        console.log(
          '[auth/signupWithPassword] no session — email confirmation required, creating Plunk contact'
        );
        try {
          await createPlunkContact(data.email, undefined, {
            active: 'false',
          });
          console.log('[auth/signupWithPassword] Plunk contact created');
        } catch (plunkError) {
          console.error(
            '[auth/signupWithPassword] Failed to create Plunk contact at signup:',
            plunkError
          );
        }
        return {error: null, needsConfirmation: true};
      }

      console.log('[auth/signupWithPassword] success — session established', {
        email: data.email,
      });
      return {error: null, needsConfirmation: false};
    }
  );

/**
 * Signs out the current user on the server. Clears session cookies.
 */
export const signOutUser = createServerFn({method: 'POST'}).handler(
  async () => {
    console.log('[auth/signOutUser] start');
    const supabase = await getSupabaseServerClient();
    await supabase.auth.signOut();
    console.log('[auth/signOutUser] success');
  }
);

/**
 * Fetches the current authenticated user's profile from cookies.
 * Returns null if not authenticated. Used by beforeLoad and components.
 */
export const fetchSessionProfile = createServerFn({method: 'GET'}).handler(
  async (): Promise<z.infer<typeof Profile> | null> => {
    const user = await getRequestUser();

    if (!user) {
      return null;
    }

    const db = await prismaClient();

    const existingProfile = await db.profile.findUnique({
      where: {supabaseId: user.id},
    });

    // Upsert to handle first login (profile creation) and subsequent logins (update)
    const profile = await db.profile.upsert({
      where: {supabaseId: user.id},
      create: {
        supabaseId: user.id,
        email: user.email ?? '',
        displayName:
          user.user_metadata?.full_name ?? user.user_metadata?.name ?? null,
        avatarUrl: user.user_metadata?.avatar_url ?? null,
      },
      update: {
        email: user.email ?? undefined,
      },
    });

    // Track account creation and create Plunk contact on first login (any auth method)
    if (!existingProfile && user.email) {
      const request = getRequest();
      const context = request ? extractRequestContext(request) : undefined;
      trackEvent(
        profile.id,
        'Account Created',
        {email: user.email},
        context
      ).catch((err) =>
        console.error(
          '[auth/fetchSessionProfile] Failed to track Account Created:',
          err
        )
      );
      try {
        const name =
          user.user_metadata?.full_name ??
          user.user_metadata?.name ??
          undefined;
        await createPlunkContact(user.email, name, {active: 'true'});
      } catch (plunkError) {
        console.error(
          '[auth/fetchSessionProfile] Failed to create Plunk contact:',
          plunkError
        );
      }

      transposeSubscriptions(db, profile.id, user.email).catch((err) =>
        console.error(
          '[auth/fetchSessionProfile] Failed to transpose subscriptions:',
          err
        )
      );
    }

    const followedListCount = await db.listFollower.count({
      where: {profileId: profile.id},
    });

    return Profile.parse({...profile, followedListCount});
  }
);

const ExchangeCodeInput = z.object({
  code: z.string(),
});

/**
 * Exchanges a PKCE auth code for a session.
 * Used after OAuth redirects that include a code param.
 */
export const exchangeCodeForSession = createServerFn({method: 'POST'})
  .inputValidator((data: unknown): z.infer<typeof ExchangeCodeInput> => {
    const result = ExchangeCodeInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data}): Promise<{error: string | null}> => {
    console.debug('[auth/exchangeCodeForSession] start');

    const supabase = await getSupabaseServerClient();
    const {error} = await supabase.auth.exchangeCodeForSession(data.code);

    if (error) {
      console.error('[auth/exchangeCodeForSession] exchange failed', {
        code: error.code,
        status: error.status,
      });
      return {error: 'Authentication failed. Please try signing in again.'};
    }

    console.debug('[auth/exchangeCodeForSession] success');
    return {error: null};
  });

export const EMAIL_OTP_TYPES = [
  'signup',
  'invite',
  'magiclink',
  'recovery',
  'email_change',
  'email',
] as const;

export const VerifyTokenHashInput = z.object({
  tokenHash: z.string().min(1),
  type: z.enum(EMAIL_OTP_TYPES),
});

/**
 * Verifies an email token hash and establishes a session.
 * Used after email confirmation links that include token_hash and type params.
 * Unlike exchangeCodeForSession, this does not require a PKCE code verifier cookie,
 * making it reliable across browsers and devices.
 */
export const verifyTokenHash = createServerFn({method: 'POST'})
  .inputValidator((data: unknown): z.infer<typeof VerifyTokenHashInput> => {
    const result = VerifyTokenHashInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data}): Promise<{error: string | null}> => {
    console.debug('[auth/verifyTokenHash] start', {type: data.type});

    const supabase = await getSupabaseServerClient();

    const {error} = await supabase.auth.verifyOtp({
      token_hash: data.tokenHash,
      type: data.type,
    });

    if (error) {
      console.error('[auth/verifyTokenHash] verify failed', {
        code: error.code,
        status: error.status,
      });
      return {
        error:
          'Email verification failed. Please try again or request a new link.',
      };
    }

    console.debug('[auth/verifyTokenHash] success');
    return {error: null};
  });

const UpdateRegistrationInfoInput = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  dateOfBirth: z.coerce.date(),
  liabilityWaiverAccepted: z.boolean().optional(),
});

/**
 * Saves the user's registration info (name, DOB, waiver).
 * Called once before the user's first event registration.
 */
export const updateRegistrationInfo = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof UpdateRegistrationInfoInput> => {
      const result = UpdateRegistrationInfoInput.safeParse(data);
      if (!result.success) {
        throw new Error(result.error.errors.map((e) => e.message).join(', '));
      }
      return result.data;
    }
  )
  .handler(async ({data, context}): Promise<z.infer<typeof Profile>> => {
    console.log('[auth/updateRegistrationInfo] start');
    const {supabaseUser} = context.auth;
    console.log('[auth/updateRegistrationInfo] user authenticated', {
      userId: supabaseUser.id,
    });

    const db = await prismaClient();
    console.log('[auth/updateRegistrationInfo] updating profile');

    const profile = await db.profile.update({
      where: {supabaseId: supabaseUser.id},
      data: {
        firstName: data.firstName,
        lastName: data.lastName,
        dateOfBirth: data.dateOfBirth,
        ...(data.liabilityWaiverAccepted && {
          liabilityWaiverAcceptedAt: new Date(),
        }),
      },
    });

    console.log('[auth/updateRegistrationInfo] success', {
      profileId: profile.id,
    });
    return Profile.parse(profile);
  });

const MarkOnboardingSeenInput = z.object({
  key: z.string().min(1).max(100),
});

/**
 * Marks an onboarding step as seen for the authenticated user.
 * Merges the key into the profile's onboardingState JSONB field.
 */
export const markOnboardingSeen = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof MarkOnboardingSeenInput> => {
    const result = MarkOnboardingSeenInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}): Promise<z.infer<typeof Profile>> => {
    console.log('[auth/markOnboardingSeen] start', {key: data.key});
    const {supabaseUser} = context.auth;
    console.log('[auth/markOnboardingSeen] user authenticated', {
      userId: supabaseUser.id,
    });

    const db = await prismaClient();

    const existing = await db.profile.findUnique({
      where: {supabaseId: supabaseUser.id},
      select: {onboardingState: true},
    });
    console.log('[auth/markOnboardingSeen] existing onboardingState', {
      onboardingState: existing?.onboardingState,
    });

    const currentState =
      existing?.onboardingState &&
      typeof existing.onboardingState === 'object' &&
      !Array.isArray(existing.onboardingState)
        ? (existing.onboardingState as Record<string, boolean>)
        : {};

    const profile = await db.profile.update({
      where: {supabaseId: supabaseUser.id},
      data: {
        onboardingState: {...currentState, [data.key]: true},
      },
    });

    console.log('[auth/markOnboardingSeen] success', {
      profileId: profile.id,
      key: data.key,
    });
    return Profile.parse(profile);
  });

export const UpdateProfileInput = z
  .object({
    firstName: z.string().min(1).max(100).optional(),
    lastName: z.string().min(1).max(100).optional(),
    avatarUrl: z.string().url().optional(),
  })
  .refine(
    (data) =>
      data.firstName !== undefined ||
      data.lastName !== undefined ||
      data.avatarUrl !== undefined,
    {message: 'At least one field must be provided'}
  );

/**
 * Updates the current user's profile. Accepts a partial payload — any of
 * firstName, lastName, avatarUrl — and writes only the fields provided.
 * Reads auth from request cookies.
 */
export const updateProfile = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof UpdateProfileInput> => {
    const result = UpdateProfileInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}): Promise<z.infer<typeof Profile>> => {
    console.log('[auth/updateProfile] start');
    const {supabaseUser} = context.auth;
    console.log('[auth/updateProfile] user authenticated', {
      userId: supabaseUser.id,
    });

    const db = await prismaClient();
    console.log('[auth/updateProfile] updating profile', {
      fields: Object.keys(data),
    });

    const updateData: {
      firstName?: string;
      lastName?: string;
      avatarUrl?: string;
    } = {};
    if (data.firstName !== undefined) updateData.firstName = data.firstName;
    if (data.lastName !== undefined) updateData.lastName = data.lastName;
    if (data.avatarUrl !== undefined) updateData.avatarUrl = data.avatarUrl;

    const profile = await db.profile.update({
      where: {supabaseId: supabaseUser.id},
      data: updateData,
    });

    console.log('[auth/updateProfile] success', {profileId: profile.id});
    return Profile.parse(profile);
  });
